import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Call } from '../types/Call';

interface CallManagementContextType {
  // Current live call
  liveCall: Call | null;
  setLiveCall: (call: Call | null) => void;
  
  // Minimized call state
  isMinimized: boolean;
  setIsMinimized: (minimized: boolean) => void;
  
  // Call modal visibility
  showLiveCallModal: boolean;
  setShowLiveCallModal: (show: boolean) => void;
  
  // End call modal visibility
  showEndCallModal: boolean;
  setShowEndCallModal: (show: boolean) => void;

  // Duration in seconds at the moment user clicked "End Call" (stops timer from affecting saved duration)
  endCallDurationSnapshot: number | null;
  setEndCallDurationSnapshot: (seconds: number | null) => void;
  
  // Refresh calls list
  refreshCalls: () => void;
  setRefreshCalls: (callback: () => void) => void;
  
  // Actions
  startCall: (call: Call) => void;
  minimizeCall: () => void;
  restoreCall: () => void;
  endCall: (callId: number, summary: string, callPickedUp: boolean, durationSeconds?: number) => Promise<void>;
  cancelCall: (callId: number, reason: string) => Promise<void>;
  rescheduleCall: (callId: number, newDate: string, reason: string) => Promise<void>;
}

const CallManagementContext = createContext<CallManagementContextType | undefined>(undefined);

interface CallManagementProviderProps {
  children: ReactNode;
  onEndCall: (callId: number, summary: string, callPickedUp: boolean, durationSeconds?: number) => Promise<void>;
  onCancelCall: (callId: number, reason: string) => Promise<void>;
  onRescheduleCall: (callId: number, newDate: string, reason: string) => Promise<void>;
}

export const CallManagementProvider: React.FC<CallManagementProviderProps> = ({ 
  children, 
  onEndCall, 
  onCancelCall, 
  onRescheduleCall 
}) => {
  // Load live call from localStorage on mount
  const getStoredLiveCall = (): Call | null => {
    try {
      const stored = localStorage.getItem('leadshive_live_call');
      if (stored) {
        const call = JSON.parse(stored);
        // Check if call is still in progress (status is 'in_progress' and start time exists)
        if (call.status === 'in_progress' && call.actualStartTime) {
          return call;
        }
      }
    } catch (error) {
      console.error('Error loading live call from localStorage:', error);
    }
    return null;
  };

  const storedLiveCall = getStoredLiveCall();
  const [liveCall, setLiveCall] = useState<Call | null>(storedLiveCall);
  const [isMinimized, setIsMinimized] = useState(storedLiveCall ? true : false); // If restoring, start minimized
  const [showLiveCallModal, setShowLiveCallModal] = useState(false);
  const [showEndCallModal, setShowEndCallModal] = useState(false);
  const [endCallDurationSnapshot, setEndCallDurationSnapshot] = useState<number | null>(null);
  const [refreshCalls, setRefreshCalls] = useState<() => void>(() => () => {});

  // Persist live call to localStorage whenever it changes
  useEffect(() => {
    if (liveCall) {
      localStorage.setItem('leadshive_live_call', JSON.stringify(liveCall));
    } else {
      localStorage.removeItem('leadshive_live_call');
    }
  }, [liveCall]);

  const startCall = (call: Call) => {
    setLiveCall(call);
    setShowLiveCallModal(false); // Don't show the full modal
    setIsMinimized(true); // Immediately minimize to floating widget
    setShowEndCallModal(false);
  };

  const minimizeCall = () => {
    setIsMinimized(true);
    setShowLiveCallModal(false);
  };

  const restoreCall = () => {
    setIsMinimized(false);
    setShowLiveCallModal(true);
  };

  const handleEndCall = async (callId: number, summary: string, callPickedUp: boolean, durationSeconds?: number) => {
    await onEndCall(callId, summary, callPickedUp, durationSeconds);
    setLiveCall(null);
    setIsMinimized(false);
    setShowLiveCallModal(false);
    setShowEndCallModal(false);
    setEndCallDurationSnapshot(null);
    refreshCalls(); // Refresh calls list after ending call
  };

  const handleCancelCall = async (callId: number, reason: string) => {
    await onCancelCall(callId, reason);
    setLiveCall(null);
    setIsMinimized(false);
    setShowLiveCallModal(false);
    setShowEndCallModal(false);
    setEndCallDurationSnapshot(null);
    refreshCalls(); // Refresh calls list after canceling call
  };

  const handleRescheduleCall = async (callId: number, newDate: string, reason: string) => {
    await onRescheduleCall(callId, newDate, reason);
    setLiveCall(null);
    setIsMinimized(false);
    setShowLiveCallModal(false);
    setShowEndCallModal(false);
    setEndCallDurationSnapshot(null);
    refreshCalls(); // Refresh calls list after rescheduling call
  };

  const value: CallManagementContextType = {
    liveCall,
    setLiveCall,
    isMinimized,
    setIsMinimized,
    showLiveCallModal,
    setShowLiveCallModal,
    showEndCallModal,
    setShowEndCallModal,
    endCallDurationSnapshot,
    setEndCallDurationSnapshot,
    refreshCalls,
    setRefreshCalls,
    startCall,
    minimizeCall,
    restoreCall,
    endCall: handleEndCall,
    cancelCall: handleCancelCall,
    rescheduleCall: handleRescheduleCall,
  };

  return (
    <CallManagementContext.Provider value={value}>
      {children}
    </CallManagementContext.Provider>
  );
};

export const useCallManagement = (): CallManagementContextType => {
  const context = useContext(CallManagementContext);
  if (context === undefined) {
    throw new Error('useCallManagement must be used within a CallManagementProvider');
  }
  return context;
};
